import { toMoney, toPercent } from '@utils/Format'
import {
  formatQuantityDisplay,
  formatQuantityFull,
  hasQuantityValue,
  toPlainNumberString,
} from '@modules/consolidator/utils/format-quantity/format-quantity'
import {
  isPosicaoZeroed,
  resolveAtivoPl,
} from '@modules/consolidator/utils/posicao-pl-fallback/posicao-pl-fallback'

const SUMMARY_PERIOD_KEYS = ['mes', 'ano', '12M', 'inicio']
const RELATIVE_SUFFIX = '_relativo'

export const formatAssetsAnalysisTable = (
  walletAnalysis,
  statisticsPerAsset,
  { currency = 'real' } = {}
) => {
  const grupos = readGrupos(walletAnalysis)
  if (grupos.length === 0) return {}
  const statsMap = statisticsPerAsset || {}
  const zeroed = isPosicaoZeroed(walletAnalysis)
  const result = {}
  let totalPl = 0
  for (const grupo of grupos) {
    const pl = getGrupoPl(grupo)
    if (addGrupoToResult(result, grupo, pl, statsMap, currency, zeroed)) {
      totalPl += pl
    }
  }
  if (totalPl > 0) result.total_pl = toMoney(totalPl, 2, currency)
  return result
}

const readGrupos = (walletAnalysis) => {
  if (!walletAnalysis || !walletAnalysis.posicao_completa) return []
  const grupos = walletAnalysis.posicao_completa.grupos
  return Array.isArray(grupos) ? grupos : []
}

const getGrupoPl = (grupo) => {
  if (!grupo) return 0
  return Number.isFinite(grupo.pl) ? grupo.pl : 0
}

const addGrupoToResult = (result, grupo, pl, statsMap, currency, zeroed) => {
  if (!grupo || !grupo.grupo) return false
  const ativos = Array.isArray(grupo.ativos) ? grupo.ativos : []
  const sorted = ativos
    .slice()
    .sort(
      (a, b) =>
        (resolveAtivoPl(b, zeroed) || 0) - (resolveAtivoPl(a, zeroed) || 0)
    )
  result[grupo.grupo] = {
    pl: toMoney(pl, 2, currency),
    weight: formatPercent(grupo.peso),
    assets: sorted.map((ativo) =>
      buildAssetRow(ativo, statsMap, currency, zeroed)
    ),
  }
  const benchmark = resolveGrupoBenchmark(grupo)
  if (benchmark) result[grupo.grupo].benchmark = benchmark
  return true
}

const resolveGrupoBenchmark = (grupo) => {
  if (!grupo || typeof grupo.benchmark !== 'string') return null
  const benchmark = grupo.benchmark.trim()
  return benchmark === '' ? null : benchmark
}

const buildAssetRow = (ativo, statsMap, currency, zeroed) => {
  if (!ativo) return {}
  const key = resolveStatsKey(ativo)
  const stats = key == null ? null : statsMap[key]
  const quantidade = ativo.quantidade == null ? null : ativo.quantidade
  const hasQuantity = hasQuantityValue(quantidade)
  return {
    id: ativo.id == null ? null : ativo.id,
    id_ativo: resolveRowId(ativo),
    nome_ativo: ativo.nome_ativo || '',
    membro: ativo.membro || '',
    quantidade,
    has_quantity: hasQuantity,
    quantidade_display: hasQuantity ? formatQuantityDisplay(quantidade) : null,
    quantidade_full: hasQuantity ? formatQuantityFull(quantidade) : null,
    quantidade_raw: hasQuantity ? toPlainNumberString(quantidade) : null,
    pl: formatMoney(ativo.pl, currency),
    weight: formatPercent(ativo.peso_no_grupo),
    weight_total: formatPercent(ativo.peso_no_portfolio),
    _d_referencia_pl: ativo._d_referencia_pl || ativo.data_referencia_pl || null,
    _d_referencia_cotizacao:
      ativo._d_referencia_cotizacao || ativo.data_referencia_cotizacao || null,
    _resgatado: resolveAtivoPl(ativo, zeroed) === 0,
    summary: buildSummary(stats),
  }
}

const resolveStatsKey = (ativo) => {
  if (ativo.id_dado_ativo != null) return String(ativo.id_dado_ativo)
  if (ativo.id != null) return String(ativo.id)
  return null
}

const resolveRowId = (ativo) => {
  if (ativo.id_dado_ativo != null) return ativo.id_dado_ativo
  if (ativo.id_ativo != null) return ativo.id_ativo
  return ativo.id == null ? null : ativo.id
}

const buildSummary = (stats) => {
  if (!stats) return {}
  const summary = {}
  for (const period of SUMMARY_PERIOD_KEYS) {
    summary[period] = formatPercent(stats[period])
    summary[period + RELATIVE_SUFFIX] = formatPercent(
      stats[period + RELATIVE_SUFFIX]
    )
  }
  if (stats.data_inicio) summary.data_inicio = stats.data_inicio
  return summary
}

const formatMoney = (value, currency) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return toMoney(value, 2, currency)
}

const formatPercent = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return toPercent(value * 100)
}

